/**
 * "Where Machines Download Updates": an Enterprise AIOE is its fleet's only distribution point (JP,
 * 23 Oct 2026). Each new release is copied here as it lands and machines download it from here, never
 * from Mojo Up; a release is offered once its installers are here. The one choice is how many of the
 * newest to keep (`keepMirrored`, at least one). Without object storage the API holds them on its own
 * disk, so there is no "off".
 *
 * A list group (Layout.tsx): how many to keep as the first row, then a row per product.
 */

import type { ReleaseProduct, ReleasesOverview } from '@aioe/shared';

import { ListGroup, ListRow } from './ui.tsx';
import { byteSize, hasInstallerStorage, patchProducts, PRODUCT_NAME } from './updatesPolicy.ts';
import { WholeNumber } from './updatesFields.tsx';

export function InstallersKept({ view, busy, act }: { view: ReleasesOverview; busy: boolean; act: (f: () => Promise<unknown>) => Promise<void> }) {
  const products = view.products.filter((p) => p.product !== 'aioe');
  const ids = products.map((p) => p.product) as ReleaseProduct[];
  // One number for every product; when they differ (set before this view), show the largest.
  const keep = products.reduce((n, p) => Math.max(n, p.policy.keepMirrored), 1);
  const sized = products.filter((p) => p.mirroredBytes !== undefined);
  const used = sized.length ? sized.reduce((n, p) => n + (p.mirroredBytes ?? 0), 0) : undefined;
  const storage = hasInstallerStorage(view);
  return (
    <ListGroup
      title="Where machines download updates"
      count={products.reduce((n, p) => n + p.mirrored.length, 0) || undefined}
      description={
        storage ? (
          'Every machine downloads its updates from here, never from Mojo Up. Each release is copied here as it lands, and offered once it is.'
        ) : (
          <>
            Machines download updates from Mojo Up, because this AIOE has no storage to keep installers in. It still decides which version each ring is offered. To serve updates from here instead, give it storage: <span className="mono">AIOE_BACKUP_STORE</span>, or <span className="mono">backups.store</span> in the Helm chart.
          </>
        )
      }
    >
      {storage ? (
        <ListRow
          title="Releases to keep"
          description={`The newest of each product, kept here for machines to download.${used !== undefined ? ` ${byteSize(used)} used.` : ''}`}
          control={<WholeNumber value={keep} min={1} max={50} isDisabled={busy} ariaLabel="How many of the newest releases to keep" onChange={(keepMirrored) => void act(() => patchProducts(view, ids, { keepMirrored }))} />}
        />
      ) : null}
      {products.map((p) => {
        const size = p.mirroredBytes !== undefined && p.mirrored.length ? ` · ${byteSize(p.mirroredBytes)}` : '';
        return (
          <ListRow
            key={p.product}
            title={PRODUCT_NAME[p.product]}
            description={
              <>
                {!storage ? 'Downloaded from Mojo Up.' : 'Served from here.'}{' '}
                {storage ? (
                  <>
                    <span className="tabular-nums">
                      {p.mirrored.length} kept{size}
                    </span>
                    {p.mirrored.length > p.policy.keepMirrored ? '; older ones go within the hour unless a machine runs them.' : '.'}
                  </>
                ) : null}
              </>
            }
          />
        );
      })}
    </ListGroup>
  );
}
